import { useState, useMemo } from 'react';
import { GENRES, EDITEURS, ETATS_GENERAUX, STATUTS, LANGUES, FORMATS, LIEUX_ACHAT, SERIES_POPULAIRES } from '../utils/constants';
import { useAuth } from '../hooks/useAuth';
import { addBDsBatch } from '../utils/firestoreService';
import { emptyBD } from '../utils/bdDefaults';

const T = {
  orange:'var(--orange)', orangeD:'var(--orange-d)', yellow:'#F5C842',
  ink:'var(--ink)', bg:'var(--bg)', surface:'var(--surface)',
  border:'var(--border)', borderMd:'var(--border-md)',
  muted:'var(--muted)', subtle:'var(--subtle)', g1:'var(--surface2)',
  green:'#16A34A', red:'#DC2626',
};
const FONT    = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const DISPLAY = "'Bangers','Arial Black',cursive";

const inputStyle = {
  width:'100%', minWidth:0, padding:'10px 13px',
  border:`1.5px solid ${T.border}`, borderRadius:'9px',
  fontFamily:FONT, fontSize:'14px',
  background:T.surface, color:T.ink,
  outline:'none',
};

const Label = ({children}) => <div style={{fontSize:'11px',fontWeight:600,color:T.muted,marginBottom:'5px',textTransform:'uppercase',letterSpacing:'.05em',fontFamily:FONT}}>{children}</div>;
const Field = ({label,children,style={}}) => <div style={{marginBottom:'12px',minWidth:0,...style}}><Label>{label}</Label>{children}</div>;
const Grid2 = ({children}) => <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)',gap:'10px'}}>{children}</div>;

export default function BulkAddModal({ bds, onClose, onDone, initialSerie = '', initialNumeros = null }) {
  const fixedNums = Array.isArray(initialNumeros) && initialNumeros.length ? initialNumeros : null;
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1 = config, 2 = aperçu / sélection

  // Config commune
  const [serie, setSerie]     = useState(initialSerie);
  const [numDebut, setNumDebut] = useState('1');
  const [numFin, setNumFin]     = useState('');
  const [exclureStr, setExclureStr] = useState('');
  const [common, setCommon] = useState(() => {
    const base = {
      auteur:'', scenariste:'', dessinateur:'',
      editeur:'', genre:'', langue:'Français', format:'',
      etatGeneral:'bon', statut:'garder', lieuAchat:'',
      prixAchat:'', dateAchat:'',
    };
    // Série existante : on reprend les infos d'un tome déjà possédé
    const ref = initialSerie && (bds||[]).find(b => (b.serie||'').trim().toLowerCase() === initialSerie.trim().toLowerCase());
    if (ref) ['auteur','scenariste','dessinateur','editeur','genre','langue','format'].forEach(k => { if (ref[k]) base[k] = ref[k]; });
    return base;
  });
  const setC = (f,val) => setCommon(c=>({...c,[f]:val}));

  const [preview, setPreview] = useState([]); // [{numero, titre, included, already}]
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(null); // null | { done, total }
  const [showPaste, setShowPaste] = useState(false);
  const [pasteTxt, setPasteTxt] = useState('');
  const [lookMsg, setLookMsg] = useState('');

  // Numéros déjà présents dans la collection pour cette série (pour éviter les doublons)
  const existingNums = useMemo(() => {
    const s = serie.trim().toLowerCase();
    if (!s) return new Set();
    return new Set(
      (bds||[])
        .filter(b => (b.serie||'').trim().toLowerCase() === s)
        .map(b => String(b.numero||'').trim())
        .filter(Boolean)
    );
  }, [bds, serie]);

  const generate = () => {
    if (!serie.trim()) { alert('Indique le nom de la série.'); return; }
    let numbers;
    if (fixedNums) {
      numbers = fixedNums;
    } else {
      const start = parseInt(numDebut, 10);
      const end   = parseInt(numFin || numDebut, 10);
      if (!start || !end || end < start) { alert('Vérifie les numéros de début et de fin.'); return; }
      if (end - start + 1 > 500) { alert('500 tomes maximum en une seule fois — fais-le en plusieurs fois.'); return; }
      const excluded = new Set(exclureStr.split(',').map(s=>s.trim()).filter(Boolean));
      numbers = [];
      for (let n = start; n <= end; n++) if (!excluded.has(String(n))) numbers.push(n);
    }
    const rows = [];
    for (const n of numbers) {
      const ns = String(n);
      const already = existingNums.has(ns);
      rows.push({
        numero: ns,
        titre: `${serie.trim()} - Tome ${n}`,
        included: !already,
        already,
      });
    }
    if (!rows.length) { alert('Aucun tome à ajouter avec ces critères.'); return; }
    setPreview(rows);
    setStep(2);
  };


  // ── Vrais titres ────────────────────────────────────────────────────────────
  const lookupTitles = async () => {
    const targets = preview.map((r,i)=>({r,i})).filter(x=>x.r.included);
    if (!targets.length || looking) return;
    setLookMsg(''); setLooking({done:0,total:targets.length});
    let fromBnf = 0, fromMn = 0, errors = 0, done = 0, cursor = 0, bnfOk = false;
    const apply = (i, titre) => setPreview(p=>p.map((row,idx)=>idx===i?{...row,titre,auto:true}:row));
    const getJson = async url => {
      const res = await fetch(url);
      if (!res.ok || !(res.headers.get('content-type')||'').includes('json')) throw new Error('indisponible');
      return res.json();
    };

    // 1) BnF : une seule requête pour toute la série
    let bnf = {};
    try {
      const d = await getJson(`/api/titres?source=bnf&serie=${encodeURIComponent(serie.trim())}`);
      bnf = d.titles || {}; bnfOk = true;
    } catch {}
    const rest = [];
    for (const t of targets) {
      const titre = bnf[String(parseInt(t.r.numero,10))];
      if (titre) { fromBnf++; apply(t.i, titre); done++; }
      else rest.push(t);
    }
    setLooking({done,total:targets.length});

    // 2) manga-news pour les tomes restants
    const worker = async () => {
      while (cursor < rest.length) {
        const { r, i } = rest[cursor++];
        try {
          const d = await getJson(`/api/titres?serie=${encodeURIComponent(serie.trim())}&n=${encodeURIComponent(r.numero)}`);
          if (d.titre) { fromMn++; apply(i, d.titre); }
        } catch { errors++; }
        done++; setLooking({done,total:targets.length});
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setLooking(null);

    const found = fromBnf + fromMn;
    if (!bnfOk && errors === rest.length && rest.length) {
      setLookMsg("Le service de recherche de titres n'est pas disponible (il ne fonctionne que sur le site déployé sur Cloudflare avec le dernier build). Tu peux coller une liste à la place.");
    } else if (found) {
      setLookMsg(`✓ ${found} titre${found>1?'s':''} trouvé${found>1?'s':''} sur ${targets.length} (BnF : ${fromBnf}, manga-news : ${fromMn}).` + (targets.length-found ? ` ${targets.length-found} sans titre (gardent « ${serie.trim()} - Tome N »).` : '') + ' Vérifie-les avant de valider.');
    } else {
      setLookMsg(`Aucun titre trouvé pour « ${serie.trim()} » (BnF et manga-news). Essaie l'orthographe exacte, ou colle une liste. Certaines séries n'ont pas de titre par tome.`);
    }
  };

  const applyPasted = () => {
    const lines = pasteTxt.split('\n').map(l=>l.trim()).filter(Boolean);
    if (!lines.length) return;
    setPreview(p => {
      const byNum = {}; const plain = [];
      lines.forEach(l => {
        const m = l.match(/^(?:t(?:ome)?\.?\s*)?(\d+)\s*[-.:)–—]\s*(.+)$/i);
        if (m) byNum[m[1]] = m[2].trim(); else plain.push(l);
      });
      let k = 0;
      return p.map(r => {
        if (byNum[r.numero]) return {...r, titre:byNum[r.numero], auto:true};
        if (!Object.keys(byNum).length && k < plain.length) return {...r, titre:plain[k++], auto:true};
        return r;
      });
    });
    setShowPaste(false); setPasteTxt('');
    setLookMsg('✓ Liste appliquée. Une ligne par tome, dans l\'ordre (ou « 5 - Titre »).');
  };

  const toggleRow = i => setPreview(p=>p.map((r,idx)=>idx===i?{...r,included:!r.included}:r));
  const setTitre   = (i,val) => setPreview(p=>p.map((r,idx)=>idx===i?{...r,titre:val}:r));
  const toggleAll  = val => setPreview(p=>p.map(r=>({...r,included:val})));

  const selectedCount = preview.filter(r=>r.included).length;

  const handleSubmit = async () => {
    const toAdd = preview.filter(r=>r.included);
    if (!toAdd.length) { alert('Sélectionne au moins un tome.'); return; }
    setSaving(true);
    try {
      const items = toAdd.map(r => emptyBD({
        serie: serie.trim(),
        numero: r.numero,
        titre: r.titre,
        ...common,
      }));
      const ids = await addBDsBatch(user.uid, items);
      onDone?.(ids.length);
      onClose();
    } catch(e) {
      alert('Erreur lors de l\'ajout groupé : '+(e.message||e));
      setSaving(false);
    }
  };

  return (
    <div
      style={{position:'fixed',inset:0,background:'rgba(28,22,18,.6)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:1000,backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)'}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{
        background:T.bg, borderRadius:'22px 22px 0 0',
        border:`1px solid ${T.border}`, borderBottom:'none',
        width:'100%', maxWidth:'640px',
        maxHeight:'min(94vh, 94dvh)',
        overflowY:'auto', overflowX:'hidden', fontFamily:FONT,
        boxShadow:'0 -12px 40px rgba(0,0,0,.18)',
      }}>
        {/* Header */}
        <div style={{position:'sticky',top:0,zIndex:10,background:'var(--header-bg)',backdropFilter:'blur(10px)',WebkitBackdropFilter:'blur(10px)',borderBottom:`1px solid ${T.border}`,padding:'1rem 1.25rem',display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'22px 22px 0 0'}}>
          <div>
            <div style={{fontFamily:DISPLAY,fontSize:'1.35rem',letterSpacing:'.05em',color:T.orange}}>📚 Ajout groupé</div>
            <div style={{fontSize:'12px',color:T.muted,marginTop:'1px'}}>{step===1?'Ajoute toute une série en une fois':`${selectedCount} tome${selectedCount>1?'s':''} sélectionné${selectedCount>1?'s':''}`}</div>
          </div>
          <button onClick={onClose} style={{width:'32px',height:'32px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.surface,cursor:'pointer',fontSize:'16px',color:T.muted,display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
        </div>

        <div style={{padding:'1.25rem'}}>
          {step===1 && (
            <>
              <Field label="Série">
                <input style={inputStyle} value={serie} onChange={e=>setSerie(e.target.value)}
                  placeholder="Ex : Naruto" list="bulk-series-list"/>
                <datalist id="bulk-series-list">
                  {SERIES_POPULAIRES.map(s=><option key={s} value={s}/>)}
                </datalist>
              </Field>

              {fixedNums ? (
                <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'9px',padding:'10px 12px',fontSize:'13px',fontWeight:600,color:'#B91C1C',marginBottom:'12px',lineHeight:1.5}}>
                  {fixedNums.length} tome{fixedNums.length>1?'s':''} manquant{fixedNums.length>1?'s':''} : {fixedNums.map(n=>'T.'+n).join(', ')}
                </div>
              ) : (
                <>
                  <Grid2>
                    <Field label="Premier tome">
                      <input style={inputStyle} type="number" min="1" value={numDebut} onChange={e=>setNumDebut(e.target.value)}/>
                    </Field>
                    <Field label="Dernier tome">
                      <input style={inputStyle} type="number" min="1" value={numFin} onChange={e=>setNumFin(e.target.value)} placeholder="Ex : 72"/>
                    </Field>
                  </Grid2>

                  <Field label="Tomes à exclure (optionnel)">
                    <input style={inputStyle} value={exclureStr} onChange={e=>setExclureStr(e.target.value)}
                      placeholder="Ex : 5, 12, 13 (tomes que tu n'as pas encore)"/>
                  </Field>
                </>
              )}

              <div style={{margin:'18px 0 12px',paddingTop:'12px',borderTop:`1px solid ${T.border}`,fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em'}}>
                Informations communes à tous les tomes
              </div>

              <Grid2>
                <Field label="Scénariste">
                  <input style={inputStyle} value={common.scenariste} onChange={e=>setC('scenariste',e.target.value)}/>
                </Field>
                <Field label="Dessinateur">
                  <input style={inputStyle} value={common.dessinateur} onChange={e=>setC('dessinateur',e.target.value)}/>
                </Field>
              </Grid2>
              <Field label="Auteur (si un seul auteur)">
                <input style={inputStyle} value={common.auteur} onChange={e=>setC('auteur',e.target.value)}/>
              </Field>

              <Grid2>
                <Field label="Éditeur">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.editeur} onChange={e=>setC('editeur',e.target.value)}>
                    <option value="">— Choisir —</option>
                    {EDITEURS.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Genre">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.genre} onChange={e=>setC('genre',e.target.value)}>
                    <option value="">— Choisir —</option>
                    {GENRES.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </Grid2>

              <Grid2>
                <Field label="Langue">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.langue} onChange={e=>setC('langue',e.target.value)}>
                    {LANGUES.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Format">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.format} onChange={e=>setC('format',e.target.value)}>
                    <option value="">— Choisir —</option>
                    {FORMATS.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </Grid2>

              <Grid2>
                <Field label="État général">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.etatGeneral} onChange={e=>setC('etatGeneral',e.target.value)}>
                    {ETATS_GENERAUX.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Statut">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.statut} onChange={e=>setC('statut',e.target.value)}>
                    {STATUTS.map(o=><option key={o.value} value={o.value}>{o.emoji} {o.label}</option>)}
                  </select>
                </Field>
              </Grid2>

              <Grid2>
                <Field label="Lieu d'achat">
                  <select style={{...inputStyle,cursor:'pointer'}} value={common.lieuAchat} onChange={e=>setC('lieuAchat',e.target.value)}>
                    <option value="">— Choisir —</option>
                    {LIEUX_ACHAT.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Prix d'achat unitaire (€)">
                  <input style={inputStyle} type="number" step="0.01" value={common.prixAchat} onChange={e=>setC('prixAchat',e.target.value)}/>
                </Field>
              </Grid2>

              <Field label="Date d'achat">
                <input style={inputStyle} type="date" value={common.dateAchat} onChange={e=>setC('dateAchat',e.target.value)}/>
              </Field>

              <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'9px',padding:'10px 12px',fontSize:'13px',fontWeight:500,color:'#92400E',margin:'8px 0 4px'}}>
                💡 Tu pourras ajuster titre, ISBN, photos et infos propres à chaque tome ensuite, individuellement.
              </div>

              <button onClick={generate} style={{width:'100%',marginTop:'14px',padding:'13px',border:'none',borderRadius:'12px',background:T.orange,color:'#fff',fontFamily:FONT,fontSize:'15px',fontWeight:700,cursor:'pointer',boxShadow:'0 4px 14px rgba(240,78,26,.3)'}}>
                Voir l'aperçu →
              </button>
            </>
          )}

          {step===2 && (
            <>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                <button onClick={()=>setStep(1)} style={{border:'none',background:'transparent',color:T.muted,fontFamily:FONT,fontSize:'13px',fontWeight:600,cursor:'pointer',padding:0}}>← Modifier les critères</button>
                <div style={{display:'flex',gap:'8px'}}>
                  <button onClick={()=>toggleAll(true)}  style={{border:'none',background:'transparent',color:T.orange,fontFamily:FONT,fontSize:'12px',fontWeight:700,cursor:'pointer'}}>Tout cocher</button>
                  <button onClick={()=>toggleAll(false)} style={{border:'none',background:'transparent',color:T.muted,fontFamily:FONT,fontSize:'12px',fontWeight:700,cursor:'pointer'}}>Tout décocher</button>
                </div>
              </div>

              <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:'10px',padding:'10px 12px',marginBottom:'10px'}}>
                <div style={{fontSize:'12px',color:'#1E40AF',fontWeight:600,marginBottom:'8px',lineHeight:1.4}}>Les titres sont « {serie.trim()} - Tome N » par défaut. Tu peux récupérer les vrais titres :</div>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  <button onClick={lookupTitles} disabled={!!looking}
                    style={{flex:'1 1 150px',padding:'10px',border:'none',borderRadius:'9px',background:'#2563EB',color:'#fff',fontFamily:FONT,fontSize:'13px',fontWeight:700,cursor:'pointer',opacity:looking?.6:1}}>
                    {looking?`Recherche… ${looking.done}/${looking.total}`:'🔎 Chercher les vrais titres'}
                  </button>
                  <button onClick={()=>setShowPaste(v=>!v)}
                    style={{flex:'1 1 150px',padding:'10px',border:'1.5px solid #2563EB',borderRadius:'9px',background:'var(--surface)',color:'#2563EB',fontFamily:FONT,fontSize:'13px',fontWeight:700,cursor:'pointer'}}>
                    📋 Coller une liste
                  </button>
                </div>
                {showPaste && (
                  <div style={{marginTop:'10px'}}>
                    <textarea value={pasteTxt} onChange={e=>setPasteTxt(e.target.value)} rows={6}
                      placeholder={'Un titre par ligne, dans l\'ordre des tomes :\nRomance Dawn\nÀ l\'attaque de Baggy\n…\n\nou avec le numéro :\n5 - Le Roi des pirates'}
                      style={{width:'100%',boxSizing:'border-box',padding:'10px',border:'1.5px solid #BFDBFE',borderRadius:'9px',fontFamily:FONT,fontSize:'16px',resize:'vertical'}}/>
                    <button onClick={applyPasted} style={{marginTop:'8px',width:'100%',padding:'10px',border:'none',borderRadius:'9px',background:T.orange,color:'#fff',fontFamily:FONT,fontSize:'13px',fontWeight:700,cursor:'pointer'}}>Appliquer la liste</button>
                  </div>
                )}
                {lookMsg && <div style={{marginTop:'8px',fontSize:'12px',color:'#1E3A8A',lineHeight:1.4}}>{lookMsg}</div>}
              </div>

              <div style={{border:`1.5px solid ${T.border}`,borderRadius:'10px',overflow:'hidden'}}>
                {preview.map((r,i)=>(
                  <div key={r.numero} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',borderBottom:i<preview.length-1?`1px solid ${T.border}`:'none',background:r.already&&r.included?'#FFFBEB':T.surface}}>
                    <input type="checkbox" checked={r.included} onChange={()=>toggleRow(i)} style={{width:'18px',height:'18px',flexShrink:0,accentColor:T.orange,cursor:'pointer'}}/>
                    <div style={{width:'40px',flexShrink:0,fontSize:'12px',fontWeight:700,color:T.muted}}>T.{r.numero}</div>
                    <input value={r.titre} onChange={e=>setTitre(i,e.target.value)}
                      style={{flex:1,minWidth:0,padding:'6px 9px',border:`1px solid ${r.auto?'#86EFAC':T.border}`,borderRadius:'7px',fontFamily:FONT,fontSize:'16px',background:T.surface,color:T.ink,outline:'none'}}/>
                    {r.already && <div title="Déjà dans ta collection pour cette série" style={{fontSize:'10px',fontWeight:700,color:'#92400E',background:'#FDE68A',padding:'2px 6px',borderRadius:'5px',flexShrink:0,whiteSpace:'nowrap'}}>déjà possédé</div>}
                  </div>
                ))}
              </div>

              <button onClick={handleSubmit} disabled={saving||selectedCount===0}
                style={{width:'100%',marginTop:'16px',padding:'13px',border:'none',borderRadius:'12px',background:selectedCount===0?T.subtle:T.orange,color:'#fff',fontFamily:FONT,fontSize:'15px',fontWeight:700,cursor:selectedCount===0?'default':'pointer',boxShadow:selectedCount===0?'none':'0 4px 14px rgba(240,78,26,.3)'}}>
                {saving?'Ajout en cours…':`Ajouter ${selectedCount} tome${selectedCount>1?'s':''} à ma collection`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
